import React, { useEffect, useRef, useState } from 'react';
import './App.css';
import { clamp } from 'framer-motion';
import { FaAccessibleIcon, FaWheelchair, FaFont } from 'react-icons/fa';

const translations = {
    pl: {
        title: 'Konfiguratory',
        description: 'Strona do konfigurowania mebli',
        konfigurator: 'Konfigurator',
        konfiguratorText: 'Przedstawiamy nasz konfigurator, z którym bez problemu zaprojektujesz meble kuchenne. Konfigurator jest prosty w obsłudze i wykonany tak, aby nasi klienci podczas projektowania mogli cieszyć się szerokim dostępem opcji oraz wsparciem producenta. \nPoniżej znajduje się film, który pokazuje jak używać naszego konfiguratora i strona z konfiguratorem.',
        produkty: 'Produkty',
        produktyText: 'Przedstawiamy wam nasz katalog mebli, czyli gotowe rozwiązania szafek kuchennych.',
        kontakt: 'Kontakt',
        adres: 'Adres:',
        adresText: 'Ul. Warszawska 128 61-047 Poznań, Polska',
        tel: 'Numer telefonu:',
        telText: '+48 616 707 447',
        email: 'E-mail:',
        emailText: 'milermispl@gmail.com',
        copyright: '© konfiguratory.eu 2025',
        conf: 'Przejdź do konfiguratora',
        accessibility: 'Opcje dostępności',
        contrast: 'Wysoki kontrast',
        fontsize: 'Powiększ czcionkę',
        daltonism: 'Tryb dla daltonistów',
        skip: 'Przejdź do treści'
    },
    en: {
        title: 'Configurators',
        description: 'Site for furniture configuration',
        konfigurator: 'Configurator',
        konfiguratorText: 'Presenting our configurator, with which you can easily design kitchen furniture. The configurator is simple to use and designed to provide our clients with a wide range of options and support from the manufacturer. Below is a video that shows how to use our configurator and configurator site.',
        produkty: 'Products',
        produktyText: 'We present to you our furniture catalog, that is, ready-made kitchen cabinet solutions.',
        kontakt: 'Contact',
        adres: 'Address:',
        adresText: 'Ul. Warszawska 128 61-047 Poznan, Poland',
        tel: 'Phone number:',
        telText: '+48 616 707 447',
        email: 'Email:',
        emailText: 'milermispl@gmail.com',
        copyright: '© konfiguratory.eu 2025',
        conf: 'To configurator',
        accessibility: 'Accessibility options',
        contrast: 'High contrast',
        fontsize: 'Increase font size',
        daltonism: 'Colorblind mode',
        skip: 'Skip to content'
    }
};

function App() {
    const videoRef = useRef(null);
    const sectionsRef = useRef([]);
    const logoRef = useRef(null);
    const navItemsRef = useRef([]);
    const mainContentRef = useRef(null);
    const [language, setLanguage] = useState('pl');
    const [showAccessibilityMenu, setShowAccessibilityMenu] = useState(false);
 
    const [largeFont, setLargeFont] = useState(false);
    const [colorBlindMode, setColorBlindMode] = useState(false);
    const [showScrollButton, setShowScrollButton] = useState(false);

    // Obsługa dostępności
    useEffect(() => {
        // Skip to content link focus
        const skipLink = document.querySelector('.skip-link');
        if (skipLink) {
            skipLink.addEventListener('click', (e) => {
                e.preventDefault();
                mainContentRef.current?.focus();
            });
        }

        // Autofocus dla nagłówka po załadowaniu
        const h1 = document.querySelector('h1');
        if (h1) h1.setAttribute('tabIndex', '-1');

        // Obsługa klawiatury
        const handleKeyDown = (e) => {
            // Zamknięcie menu dostępności ESC
            if (e.key === 'Escape' && showAccessibilityMenu) {
                setShowAccessibilityMenu(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showAccessibilityMenu]);

    useEffect(() => {
        // Zmiana klas dla dostępności
        document.body.className = '';
        
        if (largeFont) document.body.classList.add('large-font');
        if (colorBlindMode) document.body.classList.add('color-blind');
    }, [largeFont, colorBlindMode]);

    useEffect(() => {
        const video = videoRef.current;   
        if (video) {
            video.muted = true; 
            video.play().catch((err) => console.log("Autoplay error:", err));
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('visible');
                } else {
                    entry.target.classList.remove('visible');
                }
            });
        }, { threshold: 0.25 });

        sectionsRef.current.forEach(section => {
            if (section) observer.observe(section);
        });

        if (logoRef.current) {
            setTimeout(() => logoRef.current.classList.add('fade-in'), 500);
        }

        if (navItemsRef.current.length > 0) {
            navItemsRef.current.forEach((item, index) => {
                setTimeout(() => item?.classList.add('slide-in-left'), index * 300);
            });
        }

        return () => {
            sectionsRef.current.forEach(section => {
                if (section) observer.unobserve(section);
            });
        };
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setShowScrollButton(window.scrollY > 300);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleLanguage = () => setLanguage(prev => prev === 'pl' ? 'en' : 'pl');
    const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
    const t = translations[language];

    return (
        <>
            {/* Skip to content link for keyboard users */}
            <a href="#main-content" className="skip-link" tabIndex="0">
                {t.skip}
            </a>

            {/* Accessibility toolbar */}
            <div className="accessibility-toolbar">
                <button 
                    onClick={() => setShowAccessibilityMenu(!showAccessibilityMenu)}
                    aria-expanded={showAccessibilityMenu}
                    aria-label={t.accessibility}
                    aria-haspopup="true"
                    className="accessibility-button"
                >
                    <FaAccessibleIcon size={24} />
                </button>
                
                {showAccessibilityMenu && (
                    <div className="accessibility-menu" role="menu">
                       
                        <button 
                            onClick={() => setLargeFont(!largeFont)}
                            aria-pressed={largeFont}
                            role="menuitem"
                        >
                            <FaFont /> {t.fontsize}
                        </button>
                        <button 
                            onClick={() => setColorBlindMode(!colorBlindMode)}
                            aria-pressed={colorBlindMode}
                            role="menuitem"
                        >
                            <FaWheelchair /> {t.daltonism}
                        </button>
                    </div>
                )}
            </div>

            <header role="banner">
                <div className="container d-flex justify-between">
                    <a href="#">
                        <img 
                            ref={logoRef} 
                            src="/imgvid/logo.png" 
                            alt="Logo firmy" 
                            className="logo"
                        />
                    </a>
                    <nav role="navigation" className="d-flex gap-4 align-items-center ml-auto">
                        <div className="an" ref={el => navItemsRef.current[0] = el}>
                            <a href="#konfigurator" tabIndex="0">{t.konfigurator}</a>
                        </div>    
                        <div className="an" ref={el => navItemsRef.current[1] = el}>
                            <a href="#produkty" tabIndex="0">{t.produkty}</a>
                        </div>
                        <div className="an" ref={el => navItemsRef.current[2] = el}>
                            <a href="#kontakt" tabIndex="0">{t.kontakt}</a>
                        </div>
                        <button 
                            className='but' 
                            onClick={toggleLanguage}
                            style={{fontSize: clamp('1.7rem','2vw','1rem')}}
                            aria-label={`Change language to ${language === 'pl' ? 'English' : 'Polish'}`}
                        >
                            {language === 'pl' ? 'EN' : 'PL'}
                        </button>
                    </nav>
                </div>
            </header>

            <main id="main-content" ref={mainContentRef} tabIndex="-1" role="main">
                <section className="hero">
                    <div className="hero-overlay">
                        <h1>{t.title}</h1>
                        <p>{t.description}</p>
                        <video 
                            autoPlay 
                            loop 
                            muted 
                            playsInline 
                            ref={videoRef}
                            aria-label="Tło wideo przedstawiające meble kuchenne"
                        >
                            <source src="/imgvid/bg.mp4" type="video/mp4"/>
                        </video>
                    </div>
                </section>

                <section 
                    id='konfigurator' 
                    className="section" 
                    ref={el => sectionsRef.current[0] = el}
                    aria-labelledby="konfigurator-heading"
                >
                    <div className='konfigurator'> 
                        <h2 id="konfigurator-heading">{t.konfigurator}</h2>
                        <p style={{ whiteSpace: 'pre-line' }}>{t.konfiguratorText}</p>
                        <div className='map lol'>
                            <iframe 
                                loading="lazy" 
                                src="https://www.youtube.com/embed/LOoqLulj5ys?si=d_8iYhYYbeWxcV1h" 
                                title="Instrukcja obsługi konfiguratora mebli" 
                                frameBorder="0" 
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                allowFullScreen
                                aria-label="Film instruktażowy"
                            ></iframe>
                        </div>
                        <h3>
                            <a 
                                className='kupsztal' 
                                href='/konfigurator.html' 
                                target='_blank' 
                                rel="noopener noreferrer"
                            >
                                {t.conf}
                            </a>
                        </h3>
                    </div>
                </section>

                <section 
                    id="produkty" 
                    className="section" 
                    ref={el => sectionsRef.current[1] = el}
                    aria-labelledby="produkty-heading"
                >
                    <div className='produkty'>
                        <h2 id="produkty-heading">{t.produkty}</h2>
                        <p>{t.produktyText}</p>
                        <a 
                            href="/imgvid/katalog_mebli.pdf" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            aria-label="Katalog mebli w formacie PDF"
                        >
                            <img 
                                src="/imgvid/okladka.png" 
                                alt="Okładka katalogu mebli" 
                                style={{
                                    maxWidth: '390px',
                                    borderRadius: '12px', 
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                                }} 
                            />
                        </a>
                    </div>
                </section>

                <section 
                    id="kontakt" 
                    className="section" 
                    ref={el => sectionsRef.current[2] = el}
                    aria-labelledby="kontakt-heading"
                >
                    <h2 id="kontakt-heading">{t.kontakt}</h2>
                    <div className='map'>
                        <iframe 
                            src="https://www.google.com/maps?q=SOFT-TECHNIK%20Ul.%20Warszawska%20128%2061-047%20Poznań,%20Polska&output=embed" 
                            width="400" 
                            height="250"  
                            allowFullScreen 
                            loading="lazy"
                            title="Mapa z lokalizacją firmy"
                            aria-label="Mapa z lokalizacją firmy"
                        ></iframe>
                    </div>
                    <address>
                        <p><strong>{t.adres}</strong> {t.adresText}</p>
                        <p><strong>{t.tel}</strong> {t.telText}</p>
                        <p><strong>{t.email}</strong> {t.emailText}</p>
                    </address>
                </section>
            </main>

            <footer role="contentinfo">
                <div>
                    <p>{t.copyright}</p>
                    <p>&copy; Copyright Softtechnik Sp. z o.o. 2025. All right reserved.</p> 
                </div>
            </footer>

            {showScrollButton && (
                <button 
                    onClick={scrollToTop} 
                    className="scroll-to-top"
                    aria-label={language === 'pl' ? 'Przewiń do góry' : 'Scroll to top'}
                >
                    ↑
                </button>
            )}
        </>
    );
}

export default App;